import type { ApolloCache } from '@apollo/client';
import type { RecommendationsExplainerInput } from '@coursera-web/recommendations-common';
import { getRecsExplainerCaptureCache } from '@coursera-web/recommendations-common';
import isEqual from 'lodash/isEqual';

import RecommendationsExplainerCollectionQuery from 'bundles/recommendations-explainer/queries/RecommendationsExplainerCollection.graphql';
import type { RecommendationsExplainerCollectionFragment } from 'bundles/recommendations-explainer/queries/__generated__/RecommendationsExplainerCollection';
import { parseKeyPrograms } from 'bundles/recommendations-explainer/utils/parseKeyPrograms';

type ExplainerCache = Pick<ApolloCache<object>, 'extract' | 'readFragment'>;

export const getExplainerConfigurationId = (
  explainer: { configuration?: unknown } | null | undefined
): string | undefined => {
  if (
    !explainer ||
    typeof explainer.configuration !== 'object' ||
    explainer.configuration === null ||
    Array.isArray(explainer.configuration)
  ) {
    return undefined;
  }

  const id = (explainer.configuration as { id?: unknown }).id;
  return typeof id === 'string' && id.length > 0 ? id : undefined;
};

export const findRecommendationCollectionCacheKey = (
  data: Record<string, unknown>,
  input: RecommendationsExplainerInput
): string | undefined => {
  if (input.programs !== undefined) {
    const exactMatch = findRecommendationCollectionCacheKeyByProgramMatch(data, input, true);
    if (exactMatch) {
      return exactMatch;
    }
  }

  // Other key fields, such as programs, are ignored in the fallback because callers may know only id and additionalInput.
  return findRecommendationCollectionCacheKeyByProgramMatch(data, input, false);
};

const findRecommendationCollectionCacheKeyByProgramMatch = (
  data: Record<string, unknown>,
  input: RecommendationsExplainerInput,
  matchPrograms: boolean
): string | undefined => {
  const prefix = 'Recommendation_Collection:';
  const programs = input.programs === null ? null : (input.programs?.map(({ id }) => ({ id })) ?? null);

  for (const cacheKey of Object.keys(data)) {
    if (!cacheKey.startsWith(prefix)) {
      continue;
    }

    let keyFields: unknown;
    try {
      keyFields = JSON.parse(cacheKey.slice(prefix.length));
    } catch {
      continue;
    }

    if (typeof keyFields !== 'object' || keyFields === null || Array.isArray(keyFields)) {
      continue;
    }

    const {
      id,
      additionalInput,
      programs: keyPrograms,
    } = keyFields as {
      id?: unknown;
      additionalInput?: unknown;
      programs?: unknown;
    };
    const normalizedKeyPrograms = parseKeyPrograms(keyPrograms);
    if (
      id === input.id &&
      isEqual(additionalInput ?? null, input.additionalInput ?? null) &&
      (!matchPrograms || (normalizedKeyPrograms !== undefined && isEqual(normalizedKeyPrograms, programs)))
    ) {
      return cacheKey;
    }
  }

  return undefined;
};

// The capture cache preserves a query response before it is merged with earlier results in the main cache.
export const readExplainerCacheSnapshot = (cache: ExplainerCache, input: RecommendationsExplainerInput) => {
  const captureCache = getRecsExplainerCaptureCache();
  const caches = [
    ...(captureCache ? [{ cache: captureCache, data: captureCache.extract() as Record<string, unknown> }] : []),
    { cache, data: cache.extract() as Record<string, unknown> },
  ];
  const findMatch = (matchPrograms: boolean) => {
    for (const candidate of caches) {
      const cacheKey = findRecommendationCollectionCacheKeyByProgramMatch(candidate.data, input, matchPrograms);
      if (cacheKey) {
        return { cacheKey, source: candidate.cache, data: candidate.data };
      }
    }

    return undefined;
  };
  const exactMatch = input.programs !== undefined ? findMatch(true) : undefined;
  const match = exactMatch ?? findMatch(false);
  const cacheKey = match?.cacheKey;
  const source = match?.source ?? cache;
  const cacheEntry = cacheKey ? (match?.data[cacheKey] as Record<string, unknown> | undefined) : undefined;
  // Distinguishes "page query never selected explainer" from "selected but server returned null".
  const explainerFieldPresent = Boolean(cacheEntry && Object.prototype.hasOwnProperty.call(cacheEntry, 'explainer'));
  const explainer = cacheKey
    ? (source.readFragment<RecommendationsExplainerCollectionFragment>({
        id: cacheKey,
        fragment: RecommendationsExplainerCollectionQuery,
        fragmentName: 'RecommendationsExplainerCollection',
        returnPartialData: true,
      })?.explainer ?? undefined)
    : undefined;

  return { cacheKey, source, explainer, explainerFieldPresent };
};

export const readExplainerConfigurationId = (cache: ExplainerCache, input: RecommendationsExplainerInput) =>
  getExplainerConfigurationId(readExplainerCacheSnapshot(cache, input).explainer);
